import React, { useState, useMemo } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { postRequest, deleteRequest } from "@react/helpers/fetch"
import ChartPanel from "./ChartPanel"

const collectDescendantIds = (id, all) => {
  const children = all.filter(p => p.parent_id === id)
  return children.reduce((acc, c) => [...acc, c.id, ...collectDescendantIds(c.id, all)], [])
}

const BG_PALETTE = [
  "#4a90d9", "#e74c3c", "#2ecc71", "#f39c12",
  "#9b59b6", "#1abc9c", "#e67e22", "#16a085",
  "#c0392b", "#2980b9", "#8e44ad", "#27ae60",
]

const PositionsPanel = ({ initialPositions, routes }) => {
  const [positions,   setPositions]  = useState(initialPositions || [])
  const [colorIndex,  setColorIndex] = useState(0)
  const [form,        setForm]       = useState({ name: "", bg_color: BG_PALETTE[0], text_color: "#ffffff", parent_id: "" })
  const [formKey,     setFormKey]    = useState(0)
  const [submitting,  setSubmitting] = useState(false)
  const [errors,      setErrors]     = useState(null)

  const chartNodes = useMemo(() =>
    positions.map(p => ({
      id:         p.id,
      name:       p.name,
      bg_color:   p.bg_color,
      text_color: p.text_color,
      parent_id:  p.parent_id
    })),
    [positions]
  )

  const positionMap = useMemo(() => {
    const map = {}
    positions.forEach(p => { map[p.id] = p.name })
    return map
  }, [positions])

  const handleCreate = (e) => {
    e.preventDefault()
    if (submitting || !form.name.trim()) return
    setSubmitting(true)
    setErrors(null)

    postRequest({
      url:  routes.organigram_positions,
      body: { organigram_position: { ...form, parent_id: form.parent_id || null } }
    })
      .then(data => {
        if (data.errors) {
          setErrors(data.errors)
        } else {
          setPositions(prev => [...prev, data])
          const nextIndex = (colorIndex + 1) % BG_PALETTE.length
          setColorIndex(nextIndex)
          setForm({ name: "", bg_color: BG_PALETTE[nextIndex], text_color: "#ffffff", parent_id: "" })
          setFormKey(k => k + 1)
        }
        setSubmitting(false)
      })
      .catch(() => {
        setErrors([I18n.t("errors.messages.oops")])
        setSubmitting(false)
      })
  }

  const handleDelete = (id) => {
    if (!confirm(I18n.t("organigram_positions.confirm_delete"))) return
    const previous = positions
    const toRemove = new Set([id, ...collectDescendantIds(id, positions)])
    setPositions(prev => prev.filter(p => !toRemove.has(p.id)))
    deleteRequest(`${routes.organigram_positions}/${id}`)
      .catch(() => setPositions(previous))
  }

  const setField = (field) => (e) => { const v = e.target.value; setForm(prev => ({ ...prev, [field]: v })) }

  return (
    <div>
      {/* Positions hierarchy chart */}
      {positions.length > 0 && (
        positions.some(p => p.parent_id)
          ? (
            <div className="border-b border-slate-100">
              <ChartPanel chartNodes={chartNodes} />
            </div>
          ) : (
            <div className="border-b border-slate-100 py-8 text-center text-sm text-slate-400">
              {I18n.t("organigram.empty_positions_chart")}
            </div>
          )
      )}

      {/* Create form */}
      <div className="px-4 py-4 border-b border-slate-200 bg-slate-50/50">
        <p className="uppercase tracking-wider text-[11px] font-medium text-slate-500 mb-3">
          {I18n.t("organigram.new_position")}
        </p>

        {errors && (
          <div className="mb-3 text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">
            {errors.join(", ")}
          </div>
        )}

        <form key={formKey} onSubmit={handleCreate} className="flex gap-3 flex-wrap items-end">
          <div className="flex flex-col gap-1">
            <label className="uppercase tracking-wider text-[11px] font-medium text-slate-500">
              {I18n.t("organigram_positions.name")}
            </label>
            <input
              type="text"
              value={form.name}
              onChange={setField("name")}
              placeholder={I18n.t("organigram_positions.name_placeholder")}
              className="border border-slate-300 bg-white rounded-md h-9 px-2 text-sm hover:border-slate-400 focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20 outline-none w-48"
              required
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="uppercase tracking-wider text-[11px] font-medium text-slate-500">
              {I18n.t("organigram_positions.bg_color")}
            </label>
            <input
              type="color"
              value={form.bg_color}
              onChange={setField("bg_color")}
              onInput={setField("bg_color")}
              className="border border-slate-300 rounded-md h-9 w-14 p-0.5 cursor-pointer"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="uppercase tracking-wider text-[11px] font-medium text-slate-500">
              {I18n.t("organigram_positions.text_color")}
            </label>
            <input
              type="color"
              value={form.text_color}
              onChange={setField("text_color")}
              onInput={setField("text_color")}
              className="border border-slate-300 rounded-md h-9 w-14 p-0.5 cursor-pointer"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="uppercase tracking-wider text-[11px] font-medium text-slate-500">
              {I18n.t("organigram_positions.parent")}
            </label>
            <select
              value={form.parent_id}
              onChange={setField("parent_id")}
              className="border border-slate-300 bg-white rounded-md h-9 px-2 text-sm hover:border-slate-400 focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20 outline-none w-48"
            >
              <option value="">{I18n.t("organigram_positions.no_parent")}</option>
              {positions.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-1.5 h-9 px-3 bg-brand-500 hover:bg-brand-600 text-white text-[13px] font-medium rounded-md border border-brand-500 hover:border-brand-600 disabled:opacity-50 focus:ring-[3px] focus:ring-brand-500/30 transition-colors"
          >
            {submitting
              ? <FontAwesomeIcon icon="spinner" spin />
              : I18n.t("organigram_positions.add")
            }
          </button>
        </form>
      </div>

      {/* Positions table */}
      {positions.length > 0 && (
        <div role="table">
          <div role="row" className="grid items-center gap-x-3 px-4 py-3 border-b border-slate-200 bg-slate-50/50" style={{ gridTemplateColumns: "1fr 1fr 80px 80px" }}>
            <span className="uppercase tracking-wider text-[11px] font-medium text-slate-500">{I18n.t("organigram_positions.name")}</span>
            <span className="uppercase tracking-wider text-[11px] font-medium text-slate-500">{I18n.t("organigram_positions.parent")}</span>
            <span className="uppercase tracking-wider text-[11px] font-medium text-slate-500">{I18n.t("organigram_positions.colors")}</span>
            <span />
          </div>
          {positions.map(pos => (
            <div key={pos.id} role="row" className="grid items-center gap-x-3 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50" style={{ gridTemplateColumns: "1fr 1fr 80px 80px" }}>
              <div className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full shrink-0 border border-slate-200"
                  style={{ backgroundColor: pos.bg_color || "#4a90d9" }}
                />
                <span className="text-sm font-medium text-slate-800">{pos.name}</span>
              </div>
              <div className="text-sm text-slate-500">
                {pos.parent_id ? positionMap[pos.parent_id] || "—" : "—"}
              </div>
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <span
                  title={pos.bg_color}
                  style={{
                    backgroundColor: pos.bg_color || "#4a90d9",
                    width: "20px", height: "20px",
                    borderRadius: "50%",
                    border: "1px solid #e2e8f0",
                    display: "inline-block",
                    flexShrink: 0
                  }}
                />
                <span
                  title={pos.text_color}
                  style={{
                    backgroundColor: pos.text_color || "#ffffff",
                    width: "20px", height: "20px",
                    borderRadius: "50%",
                    border: "1px solid #e2e8f0",
                    display: "inline-block",
                    flexShrink: 0
                  }}
                />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => handleDelete(pos.id)}
                  className="inline-flex items-center h-8 px-2.5 text-[12.5px] font-medium text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-md transition-colors"
                >
                  {I18n.t("button.delete")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default PositionsPanel
